/**
 * Человеческие названия событий аудита.
 *
 * В журнале хранится машинный ключ вида `file.downloaded`. Читателю журнала он
 * ничего не говорит, поэтому в строке показывается фраза, а ключ остаётся в
 * технических подробностях записи.
 */
const ACTION_LABELS: Record<string, string> = {
  "system_mail.sender_changed": "Изменён отправитель системных писем",
  "auth.login_succeeded": "Вход выполнен",
  "auth.login_failed": "Неудачная попытка входа",
  "auth.login_rate_limited": "Вход заблокирован частотой попыток",
  "auth.logout": "Выход из системы",
  "auth.password_reset": "Пароль сброшен",
  "auth.email_verified": "Почта подтверждена",
  "auth.two_factor_enabled": "Двухфакторная защита включена",
  "auth.two_factor_confirmed": "Двухфакторная защита подтверждена",
  "auth.two_factor_challenged": "Запрошен второй фактор",
  "auth.two_factor_failed": "Второй фактор не подошёл",
  "auth.two_factor_disabled": "Двухфакторная защита отключена",

  "user.created": "Создан пользователь",
  "user.invited": "Приглашён пользователь",
  "user.updated": "Изменён пользователь",
  "user.status_changed": "Изменён статус пользователя",
  "user.deleted": "Удалён пользователь",
  "user.restored": "Восстановлен пользователь",
  "user.avatar_updated": "Обновлён аватар",
  "user.avatar_removed": "Удалён аватар",
  "user.two_factor_requirement_changed": "Изменено требование второго фактора",
  "user.permission_overrides_replaced": "Изменены персональные права",

  "role.created": "Создана роль",
  "role.updated": "Изменена роль",
  "role.deleted": "Удалена роль",
  "role.permissions_replaced": "Изменён состав прав роли",
  "role.assigned": "Назначена роль",
  "role.revoked": "Снята роль",

  "membership.saved": "Сохранено членство",
  "membership.updated": "Изменено членство",
  "membership.deleted": "Удалено членство",
  "department.created": "Создано подразделение",
  "department.updated": "Изменено подразделение",
  "department.deleted": "Удалено подразделение",
  "scope.created": "Создана организация",
  "scope.updated": "Изменена организация",
  "scope.deleted": "Удалена организация",
  "access.rights_transferred": "Переданы права",

  "file.upload_initiated": "Начата загрузка файла",
  "file.upload_rejected": "Отклонена загрузка файла",
  "file.upload_confirmed": "Загружен файл",
  "file.trusted_stored": "Сохранён доверенный файл",
  "file.downloaded": "Скачан файл",
  "file.previewed": "Открыт предпросмотр файла",
  "file.deleted": "Удалён файл",
  "file.restored": "Восстановлен файл",

  "module.registered": "Зарегистрирован модуль",
  "module.updated": "Обновлён модуль",
  "module.enabled": "Включён модуль",
  "module.disabled": "Отключён модуль",
  "module.settings_updated": "Изменены настройки модуля",

  "document.created": "Создан документ",
  "document.updated": "Изменён документ",
  "document.registered": "Зарегистрирован документ",
  "document.read": "Документ прочитан",
  "document.control_set": "Поставлен на контроль",
  "document.control_removed": "Контролёр снят с документа",
  "document.status_changed": "Изменено состояние документа",
  "document.completion_overridden": "Исполнение подтверждено принудительно",
  "document.moved": "Документ перемещён",
  "document.participant_added": "Добавлен участник документа",
  "document.participant_removed": "Удалён участник документа",
  "document.unassigned": "Исполнитель снят с документа",
  "document.recipients_changed": "Изменены получатели документа",
  "document.addressees_changed": "Изменены адресаты документа",
  "document.internal_issued": "Выпущен внутренний документ",
  "document.file_linked": "К документу прикреплён файл",
  "document.file_unlinked": "От документа откреплён файл",
  "document.sender_resolved_manually": "Отправитель определён вручную",
  "document.category_created": "Создана рубрика документов",
  "document.category_updated": "Изменена рубрика документов",
  "document.folder_created": "Создана папка документов",
  "document.folder_updated": "Изменена папка документов",
  "document.folder_access_granted": "Выдан доступ к папке",
  "document.folder_access_revoked": "Снят доступ к папке",
  "document.scope_access_granted": "Выдан доступ организации",
  "document.scope_access_revoked": "Снят доступ организации",
  "document.material_created": "Добавлен справочный материал",

  "site.cache.invalidated": "Сброшен кэш сайта",
  "site.news.created": "Создана новость",
  "site.news.updated": "Изменена новость",
  "site.news.published": "Опубликована новость",
  "site.news.scheduled": "Новость поставлена в план публикации",
  "site.news.unpublished": "Новость снята с публикации",
  "site.news.archived": "Новость отправлена в архив",
  "site.category.created": "Создана рубрика сайта",
  "site.category.updated": "Изменена рубрика сайта",
  "site.category.deleted": "Удалена рубрика сайта",
  "site.page.created": "Создана страница сайта",
  "site.page.updated": "Изменена страница сайта",
  "site.page.archived": "Страница сайта отправлена в архив",
  "site.settings.updated": "Изменены настройки сайта",
  "site.media.created": "Загружено изображение сайта",
  "site.media.deleted": "Удалено изображение сайта",

  "audit.export.requested": "Запрошена выгрузка журнала",
  "notification.critical_created": "Создано критичное уведомление",
};

const CATEGORY_LABELS: Record<string, string> = {
  authentication: "Вход и учётные данные",
  authorization: "Доступы",
  user_management: "Пользователи",
  role_management: "Роли",
  configuration: "Настройки и сайт",
  security: "Безопасность",
  file_access: "Файлы",
  module_management: "Модули и документы",
  system: "Система",
};

const OBJECT_KIND_LABELS: Record<string, string> = {
  file: "Файл",
  user: "Пользователь",
  role: "Роль",
  scope: "Организация",
  department: "Подразделение",
  module: "Модуль",
  news: "Новость",
  page: "Страница сайта",
  media: "Изображение",
  document: "Документ",
  audit_export: "Выгрузка журнала",
  membership: "Членство",
  unknown: "Объект",
};

const RESULT_LABELS: Record<string, string> = {
  success: "Успешно",
  failure: "Ошибка",
  denied: "Отказано",
};

const SEVERITY_LABELS: Record<string, string> = {
  debug: "отладка",
  info: "обычное",
  warning: "предупреждение",
  error: "ошибка",
  critical: "критично",
};

const SOURCE_LABELS: Record<string, string> = {
  user: "действие пользователя",
  system: "система",
  scheduler: "планировщик",
  queue: "фоновая задача",
  integration: "интеграция",
  administrator: "системный администратор",
};

/**
 * Ключ, у которого нет своей записи, всё равно должен читаться. Из
 * `document.something_new` получается «Документ: something new» — не идеально,
 * но лучше сырого ключа и не требует правки словаря ради каждого нового
 * события.
 */
export function auditActionLabel(action: string): string {
  const known = ACTION_LABELS[action];
  if (known) return known;
  const [group = action, ...rest] = action.split(".");
  const tail = rest.join(" ").replace(/_/g, " ");
  const prefix = group.charAt(0).toUpperCase() + group.slice(1);
  return tail ? `${prefix}: ${tail}` : prefix;
}

export function auditCategoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

export function auditObjectKindLabel(kind: string): string {
  return OBJECT_KIND_LABELS[kind] ?? "Объект";
}

export function auditResultLabel(result: string): string {
  return RESULT_LABELS[result] ?? result;
}

export function auditSeverityLabel(severity: string): string {
  return SEVERITY_LABELS[severity] ?? severity;
}

export function auditSourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source;
}
